import { FC } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import ReusableModal from '../common/Modal';
import { Trash } from '@phosphor-icons/react';
import { deleteOutboundFileApi } from '../../utils/apis';
import { useMutation, useQueryClient } from '@tanstack/react-query';

interface OutboundFileDeleteModalProps {
  open: boolean;
  fileId: string;
  onClose: () => void;
}

const OutboundFileDeleteModal: FC<OutboundFileDeleteModalProps> = ({
  open,
  fileId,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const { mutate: deleteOutboundFile, isPending } = useMutation({
    mutationFn: deleteOutboundFileApi,
    onSuccess: async () => {
      onClose();
      // Refetch queries to get updated file list
      await queryClient.refetchQueries({
        queryKey: ['outbound-files'],
      });
    },
  });

  const handleDeleteFile = () => {
    deleteOutboundFile(fileId);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <ReusableModal
      open={open}
      onClose={onClose}
      title="Delete File"
      width={500}
      backgroundColor="rgba(231, 76, 60, 0.15);"
    >
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column' }}>
        <Typography
          fontSize={14}
          fontWeight={400}
          lineHeight={1.5}
          letterSpacing={-0.14}
          color="text.secondary"
        >
          Are you sure?
        </Typography>

        <Typography
          fontSize={14}
          fontWeight={400}
          lineHeight={1.5}
          letterSpacing={-0.14}
          color="text.secondary"
        >
          This action cannot be undone and will delete the associated data from
          variables
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 2,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              flex: 1,
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteFile}
            startIcon={
              isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Trash size={16} />
              )
            }
            disabled={isPending}
            sx={{
              textTransform: 'none',
              borderRadius: '6px',
              flex: 1,
              bgcolor: 'error.main',
              '&:hover': {
                bgcolor: 'error.dark',
              },
            }}
          >
            {isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </Box>
      </Box>
    </ReusableModal>
  );
};

export default OutboundFileDeleteModal;
